import React, { useEffect, useState } from 'react';

import { Flex, Space, Text } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { useMutation } from '@tanstack/react-query';
import { EditAvatar } from 'components/EditAvatar';
import { useUserContext } from 'contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { UserService } from 'services/user-service';
import { UpdateAvatarDto } from 'shared/interfaces/User';
import { formatInitials } from 'shared/utils/format';

export function DetailsSettings() {
  const [user, setUser] = useUserContext();
  const { t } = useTranslation();

  const [avatarUrl, setAvatarUrl] = useState(user.avatarPath);

  useEffect(() => {
    if (avatarUrl.startsWith('blob')) return;
    setAvatarUrl(user.avatarPath);
  }, [user]);

  const handleImageDrop = (file: FileWithPath) => {
    setAvatarUrl(URL.createObjectURL(file));
    saveAvatarChanges(URL.createObjectURL(file), file);
  };

  const uploadCustomAvatarMutation = useMutation({
    mutationFn: UserService.uploadCustomAvatar,
    onSuccess: (result: UpdateAvatarDto) => handleAvatarChangesSuccess(result),
    onError: () => handleAvatarChangesError()
  });

  const removeAvatarMutation = useMutation({
    mutationFn: () => UserService.removeAvatar(),
    onSuccess: (result: UpdateAvatarDto) => handleAvatarChangesSuccess(result),
    onError: () => handleAvatarChangesError()
  });

  const saveAvatarChanges = (url: string, customAvatarFile?: FileWithPath) => {
    if (!customAvatarFile && url) return;
    if (customAvatarFile) uploadCustomAvatarMutation.mutate(customAvatarFile);
    else removeAvatarMutation.mutate();
  };

  const handleAvatarChangesSuccess = (result: UpdateAvatarDto) => {
    setUser({ ...user, avatarPath: result.avatarUrl });
    setAvatarUrl('');
    NotificationService.showResult(true, t('AVATAR.UPLOAD_SUCCESS'));
  };

  const handleAvatarChangesError = () => {
    NotificationService.showResult(false, t('AVATAR.UPLOAD_ERROR'));
  };

  const onRemoveAvatar = () => {
    setAvatarUrl('');
    saveAvatarChanges('', undefined);
  };

  return (
    <>
      <Flex direction="row" align="center">
        <EditAvatar
          src={avatarUrl}
          size={80}
          radius={100}
          placeholder={formatInitials(user.firstName, user.lastName)}
          onRemove={onRemoveAvatar}
          onFileDrop={handleImageDrop}
        />

        <Space w={30} />
        <Flex direction="column">
          <Flex direction="row" align="center" mb={5}>
            <Text fw={700}>{`${t('SETTINGS.GENERAL.DETAILS.FIRST_NAME')}:`}</Text>
            <Space w={5} />
            <Text>{user.firstName}</Text>
          </Flex>
          <Flex direction="row" align="center" my={5}>
            <Text fw={700}>{`${t('SETTINGS.GENERAL.DETAILS.LAST_NAME')}:`}</Text>
            <Space w={5} />
            <Text>{user.lastName}</Text>
          </Flex>
          <Flex direction="row" align="center" my={5}>
            <Text fw={700}>{`${t('SETTINGS.GENERAL.DETAILS.EMAIL')}:`}</Text>
            <Space w={5} />
            <Text>{user.email}</Text>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
